import { useState, useRef, useEffect, useCallback } from "react";
import { MessageCircle, X, Send, Maximize2, Minimize2, Loader2, Volume2, Square, Mail, Copy, Check, FileText } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const BRIEFING_EMAIL = "invitation@celestialmindsfze.com";
const DRAFT_REQUEST_TEXT = "Please draft a private briefing request email for me, based on our conversation.";

function buildMailto(draft) {
  return `mailto:${BRIEFING_EMAIL}?subject=${encodeURIComponent(draft.subject)}&body=${encodeURIComponent(draft.body)}`;
}

function getSessionId() {
  let id = localStorage.getItem("alpha_session_id");
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem("alpha_session_id", id);
  }
  return id;
}

const CONSENT_TEXT =
  "By using Alphena, you agree not to submit confidential information. For private matters, email invitation@celestialmindsfze.com.";

export default function AlphaChat() {
  const [open, setOpen] = useState(false);
  const [fullScreen, setFullScreen] = useState(false);
  const [messages, setMessages] = useState(() => {
    try {
      const saved = localStorage.getItem("alpha_messages");
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [consented, setConsented] = useState(() => {
    return localStorage.getItem("alpha_consented") === "true";
  });
  const [playingIdx, setPlayingIdx] = useState(null);
  const [ttsLoadingIdx, setTtsLoadingIdx] = useState(null);
  const [copiedIdx, setCopiedIdx] = useState(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const sessionId = useRef(getSessionId());
  const audioRef = useRef(null);
  const blobUrlRef = useRef(null);
  const chunkQueueRef = useRef([]);  // queued audio blobs for chunked playback
  const stoppedRef = useRef(false);  // flag to cancel queued chunks

  // Persist messages to localStorage
  useEffect(() => {
    if (messages.length > 0) {
      localStorage.setItem("alpha_messages", JSON.stringify(messages));
    }
  }, [messages]);

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      stoppedRef.current = true;
      if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
      if (blobUrlRef.current) { URL.revokeObjectURL(blobUrlRef.current); blobUrlRef.current = null; }
      chunkQueueRef.current.forEach((url) => URL.revokeObjectURL(url));
      chunkQueueRef.current = [];
    };
  }, []);

  // Stop audio when panel closes
  useEffect(() => {
    if (!open) {
      stoppedRef.current = true;
      if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
      if (blobUrlRef.current) { URL.revokeObjectURL(blobUrlRef.current); blobUrlRef.current = null; }
      chunkQueueRef.current.forEach((url) => URL.revokeObjectURL(url));
      chunkQueueRef.current = [];
      setPlayingIdx(null);
      setTtsLoadingIdx(null);
    }
  }, [open]);

  // Auto-scroll to bottom
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  // Focus input when panel opens
  useEffect(() => {
    if (open && consented && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open, consented]);

  const sendMessage = useCallback(async (overrideText) => {
    const text = (typeof overrideText === "string" ? overrideText : input).trim();
    if (!text || loading) return;

    const userMsg = { role: "user", text };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setLoading(true);

    try {
      const res = await fetch(`${API_URL}/api/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: text, session_id: sessionId.current }),
      });

      if (res.status === 429) {
        setMessages((prev) => [
          ...prev,
          { role: "assistant", text: "You've reached the message limit. Please wait a moment before continuing." },
        ]);
        return;
      }

      if (!res.ok) throw new Error("Request failed");

      const data = await res.json();
      setMessages((prev) => [...prev, { role: "assistant", text: data.reply, draft: data.draft || null }]);
    } catch {
      setMessages((prev) => [
        ...prev,
        { role: "assistant", text: "I'm momentarily unavailable. Please try again shortly." },
      ]);
    } finally {
      setLoading(false);
    }
  }, [input, loading]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const copyDraft = useCallback(async (draft, idx) => {
    const text = `Subject: ${draft.subject}\n\n${draft.body}`;
    let ok = false;
    try {
      await navigator.clipboard.writeText(text);
      ok = true;
    } catch {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      try { ok = document.execCommand("copy"); } catch { ok = false; }
      document.body.removeChild(ta);
    }
    if (ok) {
      setCopiedIdx(idx);
      setTimeout(() => setCopiedIdx((c) => (c === idx ? null : c)), 2000);
    }
  }, []);

  const lastMsg = messages[messages.length - 1];
  const showDraftChip = consented && !loading && messages.length >= 3 && !(lastMsg && lastMsg.draft);

  const stopPlayback = useCallback(() => {
    stoppedRef.current = true;
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }
    // Revoke any queued blob URLs
    chunkQueueRef.current.forEach((url) => URL.revokeObjectURL(url));
    chunkQueueRef.current = [];
    setPlayingIdx(null);
    setTtsLoadingIdx(null);
  }, []);

  const readAloud = useCallback(async (text, idx) => {
    // If already playing this message, stop it
    if (playingIdx === idx || ttsLoadingIdx === idx) {
      stopPlayback();
      return;
    }

    // Stop any current playback
    stopPlayback();
    stoppedRef.current = false;

    // Split text into sentence chunks for faster first-audio
    const sentences = text.match(/[^.!?]+[.!?]+\s*/g) || [text];
    const chunks = [];
    let current = "";
    for (const s of sentences) {
      if ((current + s).length > 200 && current) {
        chunks.push(current.trim());
        current = s;
      } else {
        current += s;
      }
    }
    if (current.trim()) chunks.push(current.trim());

    setTtsLoadingIdx(idx);

    // Fetch a single chunk's audio
    const fetchChunkAudio = async (chunkText) => {
      const res = await fetch(`${API_URL}/api/tts`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: chunkText }),
      });
      if (!res.ok) throw new Error("TTS request failed");
      const buf = await res.arrayBuffer();
      return URL.createObjectURL(new Blob([buf], { type: "audio/mpeg" }));
    };

    // Play a blob URL, returns a promise that resolves when playback ends
    const playUrl = (url) =>
      new Promise((resolve, reject) => {
        if (stoppedRef.current) { URL.revokeObjectURL(url); reject(new Error("stopped")); return; }
        const audio = new Audio();
        audioRef.current = audio;
        blobUrlRef.current = url;
        audio.onended = () => { URL.revokeObjectURL(url); resolve(); };
        audio.onerror = () => { URL.revokeObjectURL(url); reject(new Error("playback error")); };
        audio.src = url;
        audio.load();
        audio.play().catch(reject);
      });

    try {
      // Fetch first chunk — this is what the user waits for
      const firstUrl = await fetchChunkAudio(chunks[0]);
      if (stoppedRef.current) { URL.revokeObjectURL(firstUrl); return; }

      setTtsLoadingIdx(null);
      setPlayingIdx(idx);

      // Start pre-fetching remaining chunks in background
      const prefetchPromises = chunks.slice(1).map((c) => fetchChunkAudio(c));

      // Play first chunk
      await playUrl(firstUrl);

      // Play remaining chunks sequentially
      for (let i = 0; i < prefetchPromises.length; i++) {
        if (stoppedRef.current) break;
        const url = await prefetchPromises[i];
        if (stoppedRef.current) { URL.revokeObjectURL(url); break; }
        await playUrl(url);
      }
    } catch {
      // stopped or error — already cleaned up
    } finally {
      if (!stoppedRef.current) {
        setPlayingIdx(null);
      }
      audioRef.current = null;
      blobUrlRef.current = null;
    }
  }, [playingIdx, ttsLoadingIdx, stopPlayback]);

  const startChat = () => {
    setConsented(true);
    localStorage.setItem("alpha_consented", "true");
    if (messages.length === 0) {
      setMessages([
        {
          role: "assistant",
          text: "Welcome.\nI am Alphena, the AI interface of Celestial Minds\u2122 FZE.\n\nCelestial Minds operates as a private innovation institution designing next-generation systems across artificial intelligence, infrastructure, governance, and human advancement.\n\nMuch of our work remains deliberately undisclosed. However, I can provide high-level orientation, direct you to the appropriate channels, or assist in arranging a private briefing.\n\nMay I ask \u2014 are you visiting as an investor, institutional partner, or contributor?",
        },
      ]);
    }
  };

  // Panel sizing
  const panelClass = fullScreen
    ? "fixed inset-0 z-[10000]"
    : "fixed bottom-24 right-6 w-[400px] max-w-[calc(100vw-2rem)] h-[560px] max-h-[calc(100vh-8rem)] z-[10000] rounded-lg";

  if (!open) {
    return (
      <button
        data-testid="alpha-chat-bubble"
        onClick={() => setOpen(true)}
        className="fixed bottom-6 right-6 z-[10000] w-14 h-14 rounded-full flex items-center justify-center shadow-lg transition-all duration-300 hover:scale-110 group"
        style={{
          background: "linear-gradient(135deg, #c9a227 0%, #a88420 100%)",
        }}
        aria-label="Open Alphena assistant"
      >
        <MessageCircle className="w-6 h-6 text-[#050505]" strokeWidth={2} />
        {/* Pulse ring */}
        <span className="absolute inset-0 rounded-full animate-ping opacity-20" style={{ background: "#c9a227" }} />
      </button>
    );
  }

  return (
    <div className={panelClass} data-testid="alpha-chat-panel">
      <div
        className="flex flex-col h-full overflow-hidden"
        style={{
          background: fullScreen
            ? "linear-gradient(180deg, #060e1c 0%, #0a1628 100%)"
            : "linear-gradient(180deg, #060e1c 0%, #0a1628 100%)",
          border: fullScreen ? "none" : "1px solid rgba(201,162,39,0.25)",
          borderRadius: fullScreen ? 0 : "0.75rem",
        }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-5 py-3 shrink-0"
          style={{
            borderBottom: "1px solid rgba(201,162,39,0.15)",
            background: "rgba(10,22,40,0.95)",
          }}
        >
          <div className="flex items-center gap-3">
            <div
              className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold"
              style={{ background: "rgba(201,162,39,0.15)", color: "#c9a227", fontFamily: "'Cinzel', serif" }}
            >
              A
            </div>
            <div>
              <p className="text-sm font-medium text-white" style={{ fontFamily: "'Cinzel', serif" }}>Alphena</p>
              <p className="text-[10px] tracking-widest uppercase" style={{ color: "rgba(201,162,39,0.6)" }}>
                Celestial Minds Intelligence
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              data-testid="alpha-chat-fullscreen"
              onClick={() => setFullScreen((f) => !f)}
              className="p-2 rounded-md transition-colors hover:bg-white/5"
              aria-label={fullScreen ? "Exit full screen" : "Full screen"}
            >
              {fullScreen ? (
                <Minimize2 className="w-4 h-4 text-white/50" />
              ) : (
                <Maximize2 className="w-4 h-4 text-white/50" />
              )}
            </button>
            <button
              data-testid="alpha-chat-close"
              onClick={() => { setOpen(false); setFullScreen(false); }}
              className="p-2 rounded-md transition-colors hover:bg-white/5"
              aria-label="Close"
            >
              <X className="w-4 h-4 text-white/50" />
            </button>
          </div>
        </div>

        {/* Body */}
        {!consented ? (
          /* Consent screen */
          <div className="flex-1 flex flex-col items-center justify-center px-8 text-center gap-6">
            <div
              className="w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold"
              style={{ background: "rgba(201,162,39,0.1)", color: "#c9a227", fontFamily: "'Cinzel', serif" }}
            >
              A
            </div>
            <h3 className="text-lg text-white" style={{ fontFamily: "'Cinzel', serif" }}>
              Welcome to Alphena
            </h3>
            <p className="text-xs leading-relaxed" style={{ color: "rgba(255,255,255,0.5)" }}>
              {CONSENT_TEXT}
            </p>
            <p className="text-[10px]" style={{ color: "rgba(201,162,39,0.4)" }}>
              Alphena is informational and does not constitute investment advice. Sensitive matters are handled exclusively via email.
            </p>
            <button
              data-testid="alpha-chat-consent-btn"
              onClick={startChat}
              className="px-8 py-2.5 text-sm tracking-wider uppercase transition-all duration-300 hover:opacity-90"
              style={{
                background: "#c9a227",
                color: "#050505",
                fontFamily: "'Cinzel', serif",
                fontWeight: 500,
                borderRadius: "2px",
              }}
            >
              Begin Conversation
            </button>
          </div>
        ) : (
          <>
            {/* Messages */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-4" style={{ scrollBehavior: "smooth" }}>
              {messages.map((msg, i) => (
                <div
                  key={i}
                  className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                  data-testid={msg.role === "user" ? "alpha-user-message" : "alpha-assistant-message"}
                >
                  <div
                    className="max-w-[85%] px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap"
                    style={
                      msg.role === "user"
                        ? {
                            background: "rgba(201,162,39,0.12)",
                            border: "1px solid rgba(201,162,39,0.2)",
                            borderRadius: "12px 12px 2px 12px",
                            color: "#e8d5a3",
                          }
                        : {
                            background: "rgba(255,255,255,0.04)",
                            border: "1px solid rgba(255,255,255,0.06)",
                            borderRadius: "12px 12px 12px 2px",
                            color: "rgba(255,255,255,0.85)",
                          }
                    }
                  >
                    {msg.text}
                    {msg.role === "assistant" && msg.draft && (
                      <div
                        className="mt-3 px-4 py-3"
                        style={{
                          border: "1px solid rgba(201,162,39,0.3)",
                          background: "rgba(201,162,39,0.06)",
                          borderRadius: "6px",
                        }}
                        data-testid={`alpha-briefing-draft-${i}`}
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <FileText className="w-3.5 h-3.5" style={{ color: "#c9a227" }} />
                          <p className="text-[10px] uppercase tracking-[0.2em]" style={{ color: "#c9a227", fontFamily: "'Cinzel', serif" }}>
                            Private Briefing Request
                          </p>
                        </div>
                        <p className="text-xs mb-2" style={{ color: "rgba(255,255,255,0.85)" }} data-testid={`alpha-briefing-subject-${i}`}>
                          <span style={{ color: "rgba(255,255,255,0.4)" }}>Subject: </span>
                          {msg.draft.subject}
                        </p>
                        <p className="text-xs whitespace-pre-wrap leading-relaxed" style={{ color: "rgba(255,255,255,0.7)" }} data-testid={`alpha-briefing-body-${i}`}>
                          {msg.draft.body}
                        </p>
                        <div className="flex flex-wrap items-center gap-2 mt-3">
                          <a
                            href={buildMailto(msg.draft)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] uppercase tracking-wider transition-opacity hover:opacity-90"
                            style={{ background: "#c9a227", color: "#050505", borderRadius: "2px", fontFamily: "'Cinzel', serif" }}
                            data-testid={`alpha-briefing-mailto-${i}`}
                          >
                            <Mail className="w-3 h-3" />
                            Open in your email client
                          </a>
                          <button
                            onClick={() => copyDraft(msg.draft, i)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] uppercase tracking-wider transition-colors hover:bg-white/5"
                            style={{ border: "1px solid rgba(201,162,39,0.4)", color: "#c9a227", borderRadius: "2px" }}
                            data-testid={`alpha-briefing-copy-${i}`}
                          >
                            {copiedIdx === i ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                            {copiedIdx === i ? "Copied" : "Copy"}
                          </button>
                        </div>
                        <p className="text-[10px] mt-2" style={{ color: "rgba(255,255,255,0.3)" }}>
                          Nothing is sent from this chat. The draft opens in your own email application.
                        </p>
                      </div>
                    )}
                    {msg.role === "assistant" && (
                      <button
                        data-testid={`alpha-read-aloud-${i}`}
                        onClick={() => readAloud(msg.text, i)}
                        disabled={ttsLoadingIdx !== null && ttsLoadingIdx !== i}
                        className="mt-2 flex items-center gap-1.5 transition-all duration-200 hover:opacity-100 disabled:opacity-20"
                        style={{ opacity: playingIdx === i || ttsLoadingIdx === i ? 1 : 0.4, color: "#c9a227" }}
                        aria-label={playingIdx === i ? "Stop reading" : ttsLoadingIdx === i ? "Loading audio" : "Read aloud"}
                      >
                        {ttsLoadingIdx === i ? (
                          <>
                            <Loader2 className="w-3 h-3 animate-spin" />
                            <span className="text-[10px] tracking-wider uppercase">Loading...</span>
                          </>
                        ) : playingIdx === i ? (
                          <>
                            <Square className="w-3 h-3" fill="#c9a227" />
                            <span className="text-[10px] tracking-wider uppercase">Stop</span>
                          </>
                        ) : (
                          <>
                            <Volume2 className="w-3 h-3" />
                            <span className="text-[10px] tracking-wider uppercase">Read Aloud</span>
                          </>
                        )}
                      </button>
                    )}
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex justify-start" data-testid="alpha-loading">
                  <div
                    className="px-4 py-3 flex items-center gap-2"
                    style={{
                      background: "rgba(255,255,255,0.04)",
                      border: "1px solid rgba(255,255,255,0.06)",
                      borderRadius: "12px 12px 12px 2px",
                    }}
                  >
                    <Loader2 className="w-4 h-4 animate-spin" style={{ color: "#c9a227" }} />
                    <span className="text-xs" style={{ color: "rgba(255,255,255,0.4)" }}>
                      Alphena is thinking...
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Input */}
            <div
              className="shrink-0 px-4 py-3"
              style={{ borderTop: "1px solid rgba(201,162,39,0.1)", background: "rgba(10,22,40,0.6)" }}
            >
              {showDraftChip && (
                <button
                  data-testid="alpha-draft-briefing-btn"
                  onClick={() => sendMessage(DRAFT_REQUEST_TEXT)}
                  className="mb-2 inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] uppercase tracking-wider transition-colors hover:bg-white/5"
                  style={{ border: "1px solid rgba(201,162,39,0.3)", color: "#c9a227", borderRadius: "999px" }}
                >
                  <FileText className="w-3 h-3" />
                  Draft a private briefing request
                </button>
              )}
              <div className="flex items-end gap-2">
                <textarea
                  ref={inputRef}
                  data-testid="alpha-chat-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Type your message..."
                  rows={1}
                  className="flex-1 resize-none bg-transparent text-sm text-white placeholder:text-white/30 focus:outline-none"
                  style={{ maxHeight: "100px", lineHeight: "1.5" }}
                  disabled={loading}
                />
                <button
                  data-testid="alpha-chat-send"
                  onClick={sendMessage}
                  disabled={!input.trim() || loading}
                  className="p-2 rounded-md transition-all duration-200 disabled:opacity-30"
                  style={{ background: input.trim() ? "rgba(201,162,39,0.15)" : "transparent" }}
                  aria-label="Send message"
                >
                  <Send className="w-4 h-4" style={{ color: "#c9a227" }} />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
